<template>
  <tbody class="w-full">
    <tr
      v-for="(row, index) of data"
      :key="`row${index}`"
      class="h-14 text-n-800 dark:text-n-0 bg-n-0 dark:bg-transparent border-b border-b-n-50 dark:border-n-700"
      :class="{
        'hover:bg-n-10 cursor-pointer': hasClickableRows
      }"
      @click="onRowClicked(index)"
    >
      <td
        v-if="hasMultiSelect"
        @click.stop="
          () => {
            rowSelectionState[index] = !rowSelectionState[index]
            onCheckboxClick()
          }
        "
        class="px-2 h-full"
      >
        <l-checkbox
          v-model="rowSelectionState[index]"
          small
          @update:model-value="onCheckboxClick"
        />
      </td>
      <td
        v-for="column of columns"
        :key="`row${row}-column-${column.key}`"
        class="w-full h-full [&_>_*]:min-h-14 overflow-hidden"
      >
        <slot :name="`cell(${column.key})`" :row="row" :column="column" :row-index="index">
          <template v-if="Object.keys(parentSlots).includes(`cell(${column.key})`)">
            <component
              :is="parentSlots[`cell(${column.key})`]"
              :column="column"
              :row="row"
              :row-index="index"
              @checkbox="onCellCheckboxClick"
              @radio="onCellRadioClick"
              @switch="onCellSwitchClick"
            />
          </template>
          <template v-else>
            <l-table-cell
              :column="column"
              :row="row"
              :row-index="index"
              @checkbox="onCellCheckboxClick"
              @radio="onCellRadioClick"
              @switch="onCellSwitchClick"
            />
          </template>
        </slot>
      </td>
    </tr>
  </tbody>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue'

import LCheckbox from '../LCheckbox.vue'
import LTableCell from './LTableCell.vue'
import type { ParentSlots, TableDataColumn } from './types'

type Props = {
  columns: TableDataColumn[]
  data: Record<string, any>[]
  hasMultiSelect?: boolean
  parentSlots?: ParentSlots
  /** Defines if the rows are clickable */
  hasClickableRows?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  hasMultiSelect: false,
  hasClickableRows: false,
  parentSlots: () => ({})
})

const emit = defineEmits<{
  /** Emitted when a row is clicked. Prop hasClickableRows has to be setted to `true` for it to work */
  rowClicked: [rowIndex: number]
  /** Emitted when the checkbox state of the cell changes, the payload of the event is `(value: boolean, rowIndex: number, column: TableDataColumn)` */
  cellCheckboxChange: [value: boolean, rowIndex: number, column: TableDataColumn]
  /** Emitted when the radio state of the cell changes, the payload of the event is `(value: boolean, rowIndex: number, column: TableDataColumn)` */
  cellRadioChange: [value: boolean, rowIndex: number, column: TableDataColumn]
  /** Emitted when the switch state of the cell changes, the payload of the event is `(value: boolean, rowIndex: number, column: TableDataColumn)` */
  cellSwitchChange: [value: boolean, rowIndex: number, column: TableDataColumn]
}>()

const selectedRows = defineModel<number[]>('selectedRows', { default: [] })
const rowSelectionState = ref<boolean[]>([])

watch(selectedRows, () => {
  const newSelection = new Array(props.data.length).fill(false)
  selectedRows.value.forEach(rowIndex => {
    newSelection[rowIndex] = true
  })
  rowSelectionState.value = newSelection
})

const selectedIndices = computed<number[]>(() => {
  return rowSelectionState.value.reduce((acc: number[], row: boolean, idx: number): number[] => {
    if (row) acc.push(idx)
    return acc
  }, [])
})

onBeforeMount(() => {
  rowSelectionState.value = new Array(props.data.length).fill(false)
})

function onRowClicked(index: number): void {
  if (props.hasClickableRows) {
    emit('rowClicked', index)
  }
}

function onCheckboxClick(): void {
  selectedRows.value = selectedIndices.value
}

function onCellCheckboxClick(value: boolean, rowIndex: number, column: TableDataColumn): void {
  emit('cellCheckboxChange', value, rowIndex, column)
}

function onCellRadioClick(value: boolean, rowIndex: number, column: TableDataColumn): void {
  emit('cellRadioChange', value, rowIndex, column)
}

function onCellSwitchClick(value: boolean, rowIndex: number, column: TableDataColumn): void {
  emit('cellSwitchChange', value, rowIndex, column)
}
</script>
